import axios from "axios";
import Cookies from "js-cookie";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Card,
  Input,
  Button,
  Space,
  Empty,
  Typography,
  Spin,
  Alert,
} from "antd";
import {
  SearchOutlined,
} from "@ant-design/icons";

import { refresh } from "../../store/adminSlice";
import SearchItemDetails from "./search-product-item";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

const SearchProduct = ({ wege_order_id, getOrder, isDarkMode = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [id, setId] = useState("");
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const getData = async (searchId) => {
    const targetId = (searchId ?? id)?.toString().trim();
    if (!targetId) {
      toast.error(t("orderDetails.enterProductIdError", "Please enter a product ID"));
      return;
    }

    setLoading(true);
    setErrorMsg("");

    try {
      const res = await axios.get(`/product/api/v1/products/admin/${targetId}/`);
      setItem(res.data);
      toast.success(t("orderDetails.productLoaded", "Product loaded successfully"));
    } catch (e) {
      console.error("Error fetching product:", e);
      if (e.response?.status === 401 && Cookies.get("logss")) {
        dispatch(refresh()).then(() => getData(targetId));
        return;
      } else if (e.response?.status === 401 && !Cookies.get("logss")) {
        navigate("/");
        return;
      }

      if (e.response?.status === 404) {
        const notFoundMsg = t("orderDetails.productNotFound", { id: targetId, defaultValue: `Product with ID "${targetId}" was not found.` });
        setErrorMsg(notFoundMsg);
        toast.error(notFoundMsg);
      } else {
        const msg = e.response?.data?.message || t("orderDetails.failedLoadProduct", "Failed to load product details");
        setErrorMsg(msg);
        toast.error(msg);
      }
      setItem(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    getData(value);
  };

  return (
    <Card
      style={{
        borderRadius: 12,
        border: isDarkMode ? "1px solid #303030" : "1px solid #e8e8e8",
        background: isDarkMode ? "#1f1f1f" : "#ffffff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
      }}
      bodyStyle={{ padding: "20px" }}
    >
      {/* Top Search Bar */}
      <div style={{ marginBottom: 20 }}>
        <Space direction="vertical" size={6} style={{ width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Text strong style={{ fontSize: 14 }}>
              <SearchOutlined style={{ marginRight: 6, color: "#1677ff" }} />
              {t("orderDetails.searchAndAddProduct", "Search & Add Product")}
            </Text>
            {item && (
              <Button
                type="link"
                size="small"
                onClick={() => {
                  setItem(null);
                  setId("");
                  setErrorMsg("");
                }}
                style={{ padding: 0 }}
              >
                {t("orderDetails.resetSelection", "Reset Selection")}
              </Button>
            )}
          </div>

          <Input.Search
            placeholder={t("orderDetails.enterProductId", "Enter Product ID...")}
            allowClear
            size="large"
            value={id}
            onChange={(e) => setId(e.target.value)}
            onSearch={handleSearch}
            enterButton={
              <Button
                type="primary"
                icon={<SearchOutlined />}
                loading={loading}
                style={{ height: 40 }}
              >
                {t("orderDetails.search", "Search")}
              </Button>
            }
            style={{ maxWidth: "100%" }}
          />
        </Space>
      </div>

      {/* Error alert */}
      {errorMsg && (
        <Alert
          message={errorMsg}
          type="error"
          showIcon
          closable
          onClose={() => setErrorMsg("")}
          style={{ marginBottom: 16, borderRadius: 8 }}
        />
      )}

      {/* Loading state */}
      {loading && (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin size="large" tip="Loading product details..." />
        </div>
      )}

      {/* Item view */}
      {!loading && item && (
        <SearchItemDetails
          itemm={item}
          wege_order_id={wege_order_id}
          getOrder={getOrder}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Empty prompt when no item searched yet */}
      {!loading && !item && !errorMsg && (
        <div
          style={{
            padding: "40px 20px",
            textAlign: "center",
            background: isDarkMode ? "#141414" : "#fafafa",
            borderRadius: 10,
            border: `1px dashed ${isDarkMode ? "#303030" : "#d9d9d9"}`,
          }}
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space orientation="vertical" size={4}>
                <Text strong style={{ fontSize: 15 }}>
                  No Product Loaded
                </Text>
                <Text orientation="secondary" style={{ fontSize: 13, color: "#8c8c8c" }}>
                  Enter a product ID above to inspect variations, check inventory, and add items to this order.
                </Text>
              </Space>
            }
          />
        </div>
      )}
    </Card>
  );
};

export default SearchProduct;
